import { reactive } from "vue";

export const t = {token: null};

export function EntityLoaderStatus(controller = null) {
    this.controller = controller;
}

Object.defineProperty(EntityLoaderStatus.prototype, "loading", {
    get() {
        return this.controller !== null;
    }
});

export function EntityLoader(entity, entityName, getUrl, authenticated = false, method = "GET", getPayload = null) {
    this.entity = entity;
    this.entityName = entityName;
    this.getUrl = getUrl;
    this.authenticated = authenticated;
    this.method = method;
    this.getPayload = getPayload;
    this.status = new EntityLoaderStatus();
}

EntityLoader.prototype.load = function(...args) {
    this.abort();

    const controller = new AbortController();
    this.status = new EntityLoaderStatus(controller);

    const callbackFunction = (args.length && (typeof args[args.length - 1] === "function")) ? args.pop() : null;
    const requestOptions = {
        method: this.method,
        headers: (this.getPayload || this.authenticated) ? {
            "Content-Type": this.getPayload ? "application/json" : undefined,
            "Authorization": this.authenticated ? ("Bearer " + t.token) : undefined
        } : undefined,
        body: this.getPayload ? JSON.stringify(this.getPayload(...args)) : undefined,
        signal: controller.signal
    };
    fetch(this.getUrl(...args), requestOptions)
        .then(response => response.json())
        .then(data => {
            if (this.status.controller === controller) {
                this.entity.splice(0, this.entity.length);
                if (data[this.entityName] != null) {
                    data[this.entityName].forEach(element => this.entity.push(element));
                } else {
                    data.forEach(element => this.entity.push(element));
                }

                this.status = new EntityLoaderStatus();

                if (callbackFunction != null) {
                    callbackFunction(this.entity);
                }
            }
        })
        .catch(error => {
            if (error.name !== "AbortError") {
                console.trace(error);
            } else {
                console.log("Loading entity aborted: " + this.entityName);
            }

            if (this.status.controller === controller) {
                this.status = new EntityLoaderStatus();
            }
        });
}

EntityLoader.prototype.abort = function() {
    if (this.status.controller !== null) {
        this.status.controller.abort();
    }
}

export const serverUrl = "https://www.waterfootprintassessmenttool.org/";

const host = serverUrl + "nessie/entities";
const countriesPath = "countries.jsp?format=json&cors=true";
const basinsPath = "basins.jsp?format=json&cors=true";
const cropsPath = "crops.jsp?format=json&cors=true";
const worldCropsPath = "world-crops.jsp?format=json&cors=true";
const countryCropsByCountryPath = "country-crops.jsp?format=json&cors=true"; // pie chart and table
const countryCropsByCropPath = "country-crops.jsp?format=json&cors=true"; // bar chart
const basinCropsByBasinPath = "basin-crops.jsp?format=json&cors=true"; // pie chart and table
const basinCropsByCropPath = "basin-crops.jsp?format=json&cors=true"; // bar chart
const countryBasinCropsPath = "country-basin-crops.jsp?format=json&cors=true"; // sustainability table and response table
const scenarioPath = "scenarios.jsp?format=json&cors=true";
const scenarioProcessesPath = "scenario-processes.jsp?format=json&cors=true";
const scenarioProcessSubstancesPath = "scenario-process-substances.jsp?format=json&cors=true";
const scenarioCropsPath = "scenario-crops.jsp?format=json&cors=true";
const scenarioCountriesPath = "scenario-countries.jsp?format=json&cors=true";
const scenarioCountryBasinCropsPath = "scenario-country-basin-crops.jsp?format=json&cors=true";

export const intervals = [
    {text: "year", value: 1},
    {text: "month", value: 2},
    {text: "week", value: 3},
    {text: "day", value: 4}
];
export const units = [
  {unitId: 1, short: "t",         long: "t (metric ton)"},
  {unitId: 2, short: "T",         long: "T (short ton)"},
  {unitId: 3, short: "kg",        long: "kg"},
  {unitId: 4, short: "lb",        long: "lbs"},
  {unitId: 5, short: "€",         long: "€"},
  {unitId: 6, short: "$",         long: "$"},
  {unitId: 7, short: "pc",        long: "pieces"},
  {unitId: 8, short: "mg/l",      long: "mg/l"},
  {unitId: 9, short: "&#xb5;g/l", long: "&#xb5;g/l"},
  {unitId: 10, short: "&#x2103;", long: "&#x2103;"}
];
export const quantityUnits = [
  {unitId: 1, short: "t",  long: "t (metric ton)"},
  {unitId: 2, short: "T",  long: "T (short ton)"},
  {unitId: 3, short: "kg", long: "kg"},
  {unitId: 4, short: "lb", long: "lbs"},
  {unitId: 5, short: "€",  long: "€"},
  {unitId: 6, short: "$",  long: "$"},
  {unitId: 7, short: "pc", long: "pieces"}
];
export const massUnits = [
  {unitId: 1, short: "t",  long: "t (metric ton)"},
  {unitId: 2, short: "T",  long: "T (short ton)"},
  {unitId: 3, short: "kg", long: "kg"},
  {unitId: 4, short: "lb", long: "lbs"}
];
export const concentrationUnits = [
  {unitId: 8, short: "mg/l",      long: "mg/l"},
  {unitId: 9, short: "&#xb5;g/l", long: "&#xb5;g/l"},
  {unitId: 10, short: "&#x2103;", long: "&#x2103;"}
];
export const months = [
    {short: "Jan", long: "January", monthId: 1},
    {short: "Feb", long: "February", monthId: 2},
    {short: "Mar", long: "March", monthId: 3},
    {short: "Apr", long: "April", monthId: 4},
    {short: "May", long: "May", monthId: 5},
    {short: "Jun", long: "June", monthId: 6},
    {short: "Jul", long: "July", monthId: 7},
    {short: "Aug", long: "August", monthId: 8},
    {short: "Sep", long: "September", monthId: 9},
    {short: "Oct", long: "October", monthId: 10},
    {short: "Nov", long: "November", monthId: 11},
    {short: "Dec", long: "December", monthId: 12},
    {short: "Avg", long: "Annual average", monthId: 101, annual: true},
    {short: "Min", long: "Lowest month", monthId: 102, annual: true},
    {short: "Max", long: "Highest month", monthId: 103, annual: true},
    {short: "Num", long: "Number of months", monthId: 104, annual: true}
];

const entityModel = reactive({
  countries: [],
  basins: [],
  crops: [],
  worldCrops: [],

  countryCropsByCountry: [],
  countryCropsByCrop: [],
  basinCropsByBasin: [],
  basinCropsByCrop: [],
  countryBasinCrops: [],

  scenarios: [],
  scenarioProcesses: [],
  scenarioProcessSubstances: [],
  scenarioCrops: [],
  scenarioCountries: [],
  scenarioCountryCrops: [],
  scenarioCountryBasinCrops: [],
});

export const countries = entityModel.countries;
export const basins = entityModel.basins;
export const crops = entityModel.crops;
export const worldCrops = entityModel.worldCrops;

export const countryCropsByCountry = entityModel.countryCropsByCountry;
export const countryCropsByCrop = entityModel.countryCropsByCrop;
export const basinCropsByBasin = entityModel.basinCropsByBasin;
export const basinCropsByCrop = entityModel.basinCropsByCrop;
export const countryBasinCrops = entityModel.countryBasinCrops;

export const scenarios = entityModel.scenarios;
export const scenarioProcesses = entityModel.scenarioProcesses;
export const scenarioProcessSubstances = entityModel.scenarioProcessSubstances;
export const scenarioCrops = entityModel.scenarioCrops;
export const scenarioCountries = entityModel.scenarioCountries;
export const scenarioCountryCrops = entityModel.scenarioCountryCrops;
export const scenarioCountryBasinCrops = entityModel.scenarioCountryBasinCrops;

export const loadBaseEntities = function() {
    fetch(`${host}/${countriesPath}`).then(response => {
        response.json().then(data => {
            data.countries.forEach(element => { countries.push(element); });
        })
    }).catch(error => {
        console.log(error);
    });

    fetch(`${host}/${basinsPath}`).then(response => {
        response.json().then(data => {
            data.basins.forEach(element => { basins.push(element); });
        })
    }).catch(error => {
        console.log(error);
    });
    
    fetch(`${host}/${cropsPath}`).then(response => {
        response.json().then(data => {
            data.processes.forEach(element => { crops.push(element); });
        })
    }).catch(error => {
        console.log(error);
    });
    
    fetch(`${host}/${worldCropsPath}`).then(response => {
        response.json().then(data => {
            data.worldCrops.forEach(element => { 
                element.value = element.blueWFt + element.greenWFt + element.greyWFt;
                worldCrops.push(element); 
            });
        })
    }).catch(error => {
        console.log(error);
    });
}

const entityLoaders = reactive({
  countryCropsByCountryLoader: new EntityLoader(countryCropsByCountry, "countryCrops", function(countryId) {
    return `${host}/${countryCropsByCountryPath}&country=${countryId}`;
  }),

  countryCropsByCropLoader: new EntityLoader(countryCropsByCrop, "countryCrops", function(cropId) {
    return `${host}/${countryCropsByCropPath}&crop=${cropId}`;
  }),

  basinCropsByBasinLoader: new EntityLoader(basinCropsByBasin, "basinCrops", function(basinId) {
    return `${host}/${basinCropsByBasinPath}&basin=${basinId}`;
  }),

  basinCropsByCropLoader: new EntityLoader(basinCropsByCrop, "basinCrops", function(cropId) {
    return `${host}/${basinCropsByCropPath}&crop=${cropId}`;
  }),

  countryBasinCropsLoader: new EntityLoader(countryBasinCrops, "countryBasinCrops", function(countryId, basinId, cropId) {
 return (countryId != null)? `${host}/${countryBasinCropsPath}&country=${countryId}&crop=${cropId}`:
        (basinId != null)? `${host}/${countryBasinCropsPath}&basin=${basinId}&crop=${cropId}`:
        `${host}/${countryBasinCropsPath}&crop=${cropId}`;
  }),

  scenarioCreator: new EntityLoader(scenarios, "scenarios", function(name, type, unitID, quantity, csvDelimiter, csvQuoteChar, csvHeaders) {
    if ((csvDelimiter != null) && (csvQuoteChar != null) && (csvHeaders != null)) {
        return `${host}/${scenarioPath}&name=${encodeURIComponent(name)}&type=${type}&unit=${unitID}&quantity=${quantity}&delimiter=${encodeURIComponent(csvDelimiter)}&quotechar=${encodeURIComponent(csvQuoteChar)}&headers=${encodeURIComponent(csvHeaders)}`;
    }
    return `${host}/${scenarioPath}&name=${encodeURIComponent(name)}&type=${type}&unit=${unitID}&quantity=${quantity}`;
  }),

  scenariosLoader: new EntityLoader(scenarios, "scenarios", function(scenarioCode, name, type, unitID, quantity) {
    if ((name != null) && (type != null) && (unitID != null) && (quantity != null)) {
      return `${host}/${scenarioPath}&scenario=${scenarioCode}&name=${encodeURIComponent(name)}&type=${type}&unit=${unitID}&quantity=${quantity}`;
    }
    return `${host}/${scenarioPath}&scenario=${scenarioCode}`;
  }),

  scenarioProcessesLoader: new EntityLoader(scenarioProcesses, "scenarioProcesses", function(scenarioCode, processId, name, abstraction, effluent, loss, csv, geometry) {
    if (scenarioCode != null) {
        if ((processId == null) && (name != null) && (abstraction != null) && (effluent != null) && (loss != null) && (geometry != null)) {
            return `${host}/${scenarioProcessesPath}&scenario=${scenarioCode}&name=${encodeURIComponent(name)}&abstraction=${abstraction}&effluent=${effluent}&loss=${loss}&csv=${csv ? encodeURIComponent(csv) : ""}&geometry=${geometry}`;  // INSERT
        }
        if ((processId != null) && (name == null) && (abstraction == null) && (effluent == null) && (loss == null) && (geometry == null)) {
            return `${host}/${scenarioProcessesPath}&scenario=${scenarioCode}&process=${processId}`;  // DELETE
        }
        return `${host}/${scenarioProcessesPath}&scenario=${scenarioCode}`;  // SELECT
    }
  }),

  scenarioProcessSubstancesLoader: new EntityLoader(scenarioProcessSubstances, "scenarioProcessSubstances", function(scenarioCode, processId, substanceId, unitId, abstractionConcentration, effluentConcentration, naturalConcentration, maxConcentration) {
    if ((scenarioCode != null) && (processId != null)) {
        if (substanceId != null) {
            if ((unitId != null) && (abstractionConcentration != null) && (effluentConcentration != null) && (naturalConcentration != null) && (maxConcentration != null)) {
                return `${host}/${scenarioProcessSubstancesPath}&scenario=${scenarioCode}&process=${processId}&substance=${substanceId}&unit=${unitId}&absconc=${abstractionConcentration}&effconc=${effluentConcentration}&natconc=${naturalConcentration}&maxconc=${maxConcentration}`;  // INSERT
            }
            if ((unitId == null) && (abstractionConcentration == null) && (effluentConcentration == null) && (naturalConcentration == null) && (maxConcentration == null)) {
                return `${host}/${scenarioProcessSubstancesPath}&scenario=${scenarioCode}&process=${processId}&substance=${substanceId}`;  // DELETE
            }
        }
        return `${host}/${scenarioProcessSubstancesPath}&scenario=${scenarioCode}&process=${processId}`;  // SELECT
    }
  }),

  scenarioCropsLoader: new EntityLoader(scenarioCrops, "scenarioCrops", function(scenarioCode) {
    return `${host}/${scenarioCropsPath}&scenario=${scenarioCode}`;
  }),

  scenarioCountriesLoader: new EntityLoader(scenarioCountries, "scenarioCountries", function(scenarioCode) {
    return `${host}/${scenarioCountriesPath}&scenario=${scenarioCode}`;
  }),

  scenarioCountryCropsCreator: new EntityLoader(scenarioCountryCrops, "scenarioCountryCrops",
    (scenarioCode, countryId, cropId, intervalId, unitId, quantity, greenWFt, blueWFt, greyWFt) => `${serverUrl}nessie/rest/assessment/scenarios/${scenarioCode}/country-crops/${countryId}/${cropId}`,
    true, "PUT",
    (scenarioCode, countryId, cropId, intervalId, unitId, quantity, greenWFt, blueWFt, greyWFt) => { return {intervalId: intervalId, unitId: unitId, quantity, greenWFt, blueWFt, greyWFt}; }
  ),

  scenarioCountryCropsRemover: new EntityLoader(scenarioCountryCrops, "scenarioCountryCrops",
    (scenarioCode, countryId, cropId) => `${serverUrl}nessie/rest/assessment/scenarios/${scenarioCode}/country-crops/${countryId}/${cropId}`,
    true, "DELETE"
  ),

  scenarioCountryCropsLoader: new EntityLoader(scenarioCountryCrops, "scenarioCountryCrops",
    scenarioCode => `${serverUrl}nessie/rest/assessment/scenarios/${scenarioCode}/country-crops`,
    true
  ),

  scenarioCountryBasinCropsLoader: new EntityLoader(scenarioCountryBasinCrops, "scenarioCountryBasinCrops", function(scenarioCode) {
    return `${host}/${scenarioCountryBasinCropsPath}&scenario=${scenarioCode}`;
  })
});

export const countryCropsByCountryLoader = entityLoaders.countryCropsByCountryLoader;
export const countryCropsByCropLoader = entityLoaders.countryCropsByCropLoader;
export const basinCropsByBasinLoader = entityLoaders.basinCropsByBasinLoader;
export const basinCropsByCropLoader = entityLoaders.basinCropsByCropLoader;
export const countryBasinCropsLoader = entityLoaders.countryBasinCropsLoader;

export const scenarioCreator = entityLoaders.scenarioCreator;
export const scenariosLoader = entityLoaders.scenariosLoader;
export const scenarioProcessesLoader = entityLoaders.scenarioProcessesLoader;
export const scenarioProcessSubstancesLoader = entityLoaders.scenarioProcessSubstancesLoader;
export const scenarioCropsLoader = entityLoaders.scenarioCropsLoader;
export const scenarioCountriesLoader = entityLoaders.scenarioCountriesLoader;
export const scenarioCountryCropsCreator = entityLoaders.scenarioCountryCropsCreator;
export const scenarioCountryCropsRemover = entityLoaders.scenarioCountryCropsRemover;
export const scenarioCountryCropsLoader = entityLoaders.scenarioCountryCropsLoader;
export const scenarioCountryBasinCropsLoader = entityLoaders.scenarioCountryBasinCropsLoader;

export function getCountry(countryID) {
    for (const country of countries) {
        if (country.countryID == countryID) {
            return country;
        }
    }
    return null;
}

export function getCountryByIso3Code(iso3Code) {
    for (const country of countries) {
        if (country.iso3Code == iso3Code) {
            return country;
        }
    }
    return null;
}

export function getBasin(basinID) {
    for (const basin of basins) {
        if (basin.basinID == basinID) {
            return basin;
        }
    }
    return null;
}

export function getCrop(cropID) {
    for (const crop of crops) {
        if (crop.cropID == cropID) {
            return crop;
        }
    }
    return null;
}

